import React, { useContext, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../../elements/Layout';
import Modal from '../../elements/Modal';
import { deleteLead, fetchLeads, fetchUsers, getProjects } from '../../utils/api/api';
import toast from 'react-hot-toast';
import AddLeadsForm from './AddLeadsForm';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api/axiosInstance';
import Select from 'react-select';
import Loading from '../../elements/Loading';
import { canAccess } from '../../utils/canAccess';
import { AuthContext } from '../../utils/context/AuthContext';

// Icons
import {
  IoIosSearch,
  IoMdOptions,
  IoMdRefresh,
  IoMdFunnel
} from 'react-icons/io';
import {
  FaUpload,
  FaFilter,
  FaPlus,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
  FaEllipsisH,
  FaBuilding,
  FaUser,
  FaUserTie,
  FaEnvelope,
  FaProjectDiagram,
  FaThermometerHalf,
  FaThermometerFull,
  FaThermometerEmpty,
  FaFileUpload,
  FaFileExcel,
  FaExclamationTriangle
} from 'react-icons/fa';
import {
  MdDeleteForever,
  MdAdd,
  MdFilterList,
  MdOutlineLeaderboard,
  MdPersonAdd
} from 'react-icons/md';
import {
  HiOutlineFilter,
  HiOutlineOfficeBuilding,
  HiOutlineFire,
  HiOutlineClipboardList
} from 'react-icons/hi';

const Leads = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [showAddLead, setShowAddLead] = useState(false);
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [file, setFile] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [assignedTo, setAssignedTo] = useState('');
  const [stats, setStats] = useState([]);
  const [leadsPerPage] = useState(40);
  const [totalPages, setTotalPages] = useState(1);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [del, setDel] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const navigate = useNavigate();

  const getLeads = async (page, size, filters = {}) => {
    setLoading(true);
    try {
      if (user?.role?.view_all_leads === false) {
        filters.assigned_to = user.id;
      }

      const payload = {
        page,
        pageSize: size,
        ...filters,
      };

      const data = await fetchLeads(payload);
      setLeads(data.leads);
      setFilteredLeads(data.leads);
      setStats(data.stats);
      setTotalPages(data.meta.totalPages);
    } catch (err) {
      toast.error('Failed to fetch Leads.');
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file before uploading.");
      return;
    }

    if (!selectedProject) {
      toast.error("Please select a project.");
      return;
    }

    const formData = new FormData();
    formData.append("excel_file", file);
    formData.append("project_id", selectedProject);
    setLoading(true);

    try {
      const response = await api.post("/leads/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("File uploaded successfully.");
      getLeads(currentPage, leadsPerPage);
      setOpen(false);
      setFile(null);
      setSelectedProject('');
    } catch (error) {
      toast.error(error.message || "Failed to upload file.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getProjects();
        setProjects(response.projects);
      } catch (error) {
        toast.error('Failed to fetch projects.');
      }
    };

    fetchProjects();
  }, []);

  const getUsers = async () => {
    try {
      const data = await fetchUsers();
      setUsers(data.users);
    } catch (err) {
      toast.error('Failed to fetch users.');
    }
  };

  const confirmDeleteLead = (id, e) => {
    if (e) e.stopPropagation();
    setLeadToDelete(id);
    setDel(true); // Open the modal
  };

  const handleDeleteLead = async () => {
    if (!leadToDelete) return;
    setIsDeleting(true);
    try {
      await deleteLead(leadToDelete);
      toast.success("Lead deleted successfully");
      getLeads(currentPage, leadsPerPage);
      setDel(false); // Close modal
    } catch (error) {
      console.error("Failed to delete lead:", error);
      toast.error("Failed to delete lead");
    } finally {
      setIsDeleting(false);
      setLeadToDelete(null);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    getLeads(currentPage, leadsPerPage);
  }, [currentPage]);

  const handleFilterChange = (newFilters) => {
    const filters = {};

    if (newFilters.status) {
      filters.status = newFilters.status;
      setStatusFilter(newFilters.status);
    }
    if (newFilters.source) {
      filters.source = newFilters.source;
      setSourceFilter(newFilters.source);
    }
    if (newFilters.project_id) {
      filters.project_id = newFilters.project_id;
      setSelectedProject(newFilters.project_id);
    }
    if (newFilters.priority) {
      filters.priority = newFilters.priority;
      setPriorityFilter(newFilters.priority);
    }
    if (newFilters.assigned_to) {
      filters.assigned_to = newFilters.assigned_to;
      setAssignedTo(newFilters.assigned_to);
    }

    getLeads(1, leadsPerPage, filters);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    const filtered = leads.filter(lead =>
      lead.title?.toLowerCase().includes(e.target.value.toLowerCase()) ||
      lead.username?.toLowerCase().includes(e.target.value.toLowerCase()) ||
      lead.email?.toLowerCase().includes(e.target.value.toLowerCase()) ||
      lead.project?.title?.toLowerCase().includes(e.target.value.toLowerCase())
    );
    setFilteredLeads(filtered);
  };

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const resetFilters = () => {
    setStatusFilter('');
    setSourceFilter('');
    setPriorityFilter('');
    setAssignedTo('');
    setSelectedProject('');
    setSearchQuery('');
    getLeads(1, leadsPerPage);
    setCurrentPage(1);
  };

  const totalLeads = stats?.find(stat => stat.total_items)?.total_items || 0;
  const hotLeads = stats?.find(stat => stat.priority === 'hot')?.total || 0;
  const warmLeads = stats?.find(stat => stat.priority === 'warm')?.total || 0;
  const coldLeads = stats?.find(stat => stat.priority === 'cold')?.total || 0;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 24 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    },
    hover: {
      y: -5,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 15
      }
    }
  };

  // Get icon for priority
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'hot':
        return <FaThermometerFull className="mr-1" />;
      case 'warm':
        return <FaThermometerHalf className="mr-1" />;
      case 'cold':
        return <FaThermometerEmpty className="mr-1" />;
      default:
        return null;
    }
  };

  return (
    <Layout text={"Leads"}>
      {loading && <Loading />}

      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="space-y-6"
      >
        {/* Header Controls */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white/80 backdrop-blur-sm p-5 rounded-2xl shadow-sm"
        >
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search leads..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200"
            />
            <IoIosSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
            >
              <HiOutlineFilter size={18} />
              <span>Filter</span>
            </motion.button>

            {canAccess(user, 'list_leads') && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors shadow-sm"
              >
                <FaFileUpload size={16} />
                <span>Import</span>
              </motion.button>
            )}

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAddLead(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-white rounded-xl hover:bg-secondary/90 transition-colors shadow-sm"
            >
              <MdPersonAdd size={18} />
              <span>Add Lead</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        {user?.role?.view_all_leads && (
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6"
          >
            <motion.div
              whileHover="hover"
              variants={cardVariants}
              className="rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100 shadow-sm overflow-hidden"
            >
              <div className="p-6 relative">
                <div className="absolute top-2 right-2 opacity-10">
                  <MdOutlineLeaderboard className="text-emerald-500 text-6xl" />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-emerald-500 rounded-xl text-white">
                    <HiOutlineClipboardList size={20} />
                  </div>
                  <h3 className="text-sm font-medium text-gray-800">Total Leads</h3>
                </div>
                <p className="text-3xl font-bold text-emerald-700">{totalLeads}</p>
                <p className="text-xs text-emerald-600 mt-2">All leads in the system</p>
              </div>
            </motion.div>

            <motion.div
              whileHover="hover"
              variants={cardVariants}
              className="rounded-2xl bg-gradient-to-br from-red-50 to-red-100 shadow-sm overflow-hidden"
            >
              <div className="p-6 relative">
                <div className="absolute top-2 right-2 opacity-10">
                  <HiOutlineFire className="text-red-500 text-6xl" />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-red-500 rounded-xl text-white">
                    <FaThermometerFull size={20} />
                  </div>
                  <h3 className="text-sm font-medium text-gray-800">Hot Leads</h3>
                </div>
                <p className="text-3xl font-bold text-red-700">{hotLeads}</p>
                <p className="text-xs text-red-600 mt-2">High-priority opportunities</p>
              </div>
            </motion.div>

            <motion.div
              whileHover="hover"
              variants={cardVariants}
              className="rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 shadow-sm overflow-hidden"
            >
              <div className="p-6 relative">
                <div className="absolute top-2 right-2 opacity-10">
                  <FaThermometerHalf className="text-amber-500 text-6xl" />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-amber-500 rounded-xl text-white">
                    <FaThermometerHalf size={20} />
                  </div>
                  <h3 className="text-sm font-medium text-gray-800">Warm Leads</h3>
                </div>
                <p className="text-3xl font-bold text-amber-700">{warmLeads}</p>
                <p className="text-xs text-amber-600 mt-2">Medium-priority leads</p>
              </div>
            </motion.div>

            <motion.div
              whileHover="hover"
              variants={cardVariants}
              className="rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 shadow-sm overflow-hidden"
            >
              <div className="p-6 relative">
                <div className="absolute top-2 right-2 opacity-10">
                  <FaThermometerEmpty className="text-blue-500 text-6xl" />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-500 rounded-xl text-white">
                    <FaThermometerEmpty size={20} />
                  </div>
                  <h3 className="text-sm font-medium text-gray-800">Cold Leads</h3>
                </div>
                <p className="text-3xl font-bold text-blue-700">{coldLeads}</p>
                <p className="text-xs text-blue-600 mt-2">Low-priority prospects</p>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Filters Panel */}
        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="bg-white/80 backdrop-blur-sm p-5 rounded-2xl shadow-sm"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium flex items-center gap-2 text-gray-700">
                  <MdFilterList /> Filter Leads
                </h3>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={resetFilters}
                  className="flex items-center gap-1 text-sm text-primary hover:text-primary/80"
                >
                  <IoMdRefresh size={16} /> Reset
                </motion.button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => handleFilterChange({ status: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-secondary/30"
                  >
                    <option value="">All Statuses</option>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="converted">Converted</option>
                    <option value="qualified">Qualified</option>
                    <option value="disqualified">Disqualified</option>
                  </select>
                  <HiOutlineClipboardList className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                </div>

                {user?.role?.view_all_leads && (
                  <div className="relative">
                    <div className="w-full pl-10 bg-gray-50 rounded-xl">
                      <Select
                        value={users.find(user => user.id === assignedTo)}
                        onChange={(selectedOption) => handleFilterChange({ assigned_to: selectedOption?.value })}
                        options={users.map(user => ({
                          label: `${user.first_name} ${user.last_name}`,
                          value: user.id
                        }))}
                        placeholder="All Users"
                        isClearable={true}
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: 'rgb(249 250 251)',
                            border: 'none',
                            boxShadow: 'none',
                            padding: '2px 0',
                            '&:hover': {
                              border: 'none'
                            }
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected
                              ? 'var(--color-secondary)'
                              : state.isFocused
                                ? 'rgba(var(--color-secondary-rgb), 0.1)'
                                : null,
                            color: state.isSelected ? 'white' : 'inherit'
                          })
                        }}
                      />
                    </div>
                    <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={16} />
                  </div>
                )}

                <div className="relative">
                  <select
                    value={selectedProject}
                    onChange={(e) => handleFilterChange({ project_id: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-secondary/30"
                  >
                    <option value="">All Projects</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.title}
                      </option>
                    ))}
                  </select>
                  <HiOutlineOfficeBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                </div>

                <div className="relative">
                  <select
                    value={sourceFilter}
                    onChange={(e) => handleFilterChange({ source: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-secondary/30"
                  >
                    <option value="">All Sources</option>
                    <option value="facebook">Facebook</option>
                    <option value="referral">Referral</option>
                    <option value="google_ads">Google Ads</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="website">Website</option>
                  </select>
                  <FaProjectDiagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={16} />
                </div>

                <div className="relative">
                  <select
                    value={priorityFilter}
                    onChange={(e) => handleFilterChange({ priority: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-secondary/30"
                  >
                    <option value="">All Priorities</option>
                    <option value="hot">Hot</option>
                    <option value="warm">Warm</option>
                    <option value="cold">Cold</option>
                  </select>
                  <FaThermometerHalf className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={16} />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Leads Table */}
        <motion.div
          variants={itemVariants}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
        >
          {loading ? (
            <div className="p-8 flex justify-center items-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full"
              />
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
              >
                <MdOutlineLeaderboard size={64} className="text-gray-300 mb-4" />
              </motion.div>
              <motion.h3
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-xl font-semibold text-gray-700 mb-2"
              >
                No Leads Found
              </motion.h3>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-gray-500 mb-6 text-center max-w-md"
              >
                There are no leads matching your current filter criteria. Try adjusting your filters or add a new lead.
              </motion.p>
              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddLead(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-secondary text-white rounded-xl shadow-sm hover:bg-secondary/90 transition-colors"
              >
                <MdPersonAdd size={18} />
                <span>Add First Lead</span>
              </motion.button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredLeads.map((lead, index) => (
                    <motion.tr
                      key={lead.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      whileHover={{ backgroundColor: "#f9fafb" }}
                      className="group cursor-pointer"
                      onClick={() => navigate(`/dashboard/leads/${lead.id}/view`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1 + (currentPage - 1) * leadsPerPage}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <MdOutlineLeaderboard className="h-5 w-5 text-primary" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{lead?.title || 'Untitled Lead'}</div>
                            <div className="text-xs text-gray-500">{lead?.source || 'Unknown source'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{lead?.username || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FaEnvelope className="mr-2 text-gray-400" size={14} />
                          <span className="text-sm text-gray-700">{lead?.email || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FaBuilding className="mr-2 text-gray-400" size={14} />
                          <span className="text-sm text-gray-700">{lead?.project?.title || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${lead?.priority === 'cold'
                              ? 'bg-blue-100 text-blue-800'
                              : lead?.priority === 'warm'
                                ? 'bg-amber-100 text-amber-800'
                                : lead?.priority === 'hot'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}
                        >
                          {getPriorityIcon(lead?.priority)}
                          {lead?.priority || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/leads/${lead.id}/view`);
                            }}
                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            <FaEye size={16} />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => confirmDeleteLead(lead.id, e)}
                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            <MdDeleteForever size={18} />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {filteredLeads.length > 0 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((currentPage - 1) * leadsPerPage) + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * leadsPerPage, filteredLeads.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredLeads.length}</span> leads
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-3 py-2 rounded-l-lg text-sm font-medium ${currentPage === 1
                          ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                          : 'text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                    >
                      <FaChevronLeft className="h-4 w-4" />
                    </motion.button>

                    {/* Show first page, ellipsis, and nearby pages */}
                    {totalPages > 5 && currentPage > 3 && (
                      <>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => paginate(1)}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          1
                        </motion.button>
                        <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white">
                          <FaEllipsisH className="h-3 w-3" />
                        </span>
                      </>
                    )}

                    {/* Current and adjacent pages */}
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <motion.button
                            key={page}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => paginate(page)}
                            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${currentPage === page
                                ? 'z-10 bg-primary text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </motion.button>
                        );
                      }
                      return null;
                    })}

                    {/* Show ellipsis and last page */}
                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <>
                        <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white">
                          <FaEllipsisH className="h-3 w-3" />
                        </span>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => paginate(totalPages)}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          {totalPages}
                        </motion.button>
                      </>
                    )}

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-3 py-2 rounded-r-lg text-sm font-medium ${currentPage === totalPages
                          ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                          : 'text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                    >
                      <FaChevronRight className="h-4 w-4" />
                    </motion.button>
                  </nav>
                </div>
              </div>

              {/* Mobile pagination */}
              <div className="flex sm:hidden justify-between w-full">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === 1
                      ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                      : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                >
                  <FaChevronLeft className="mr-2 h-4 w-4" />
                  Prev
                </motion.button>

                <div className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === totalPages
                      ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                      : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                >
                  Next
                  <FaChevronRight className="ml-2 h-4 w-4" />
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Import Modal */}
      <AnimatePresence>
        {open && (
          <Modal title="Import Leads" onClose={() => setOpen(false)}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-6"
            >
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Upload Excel File
                </h3>
                <p className="text-gray-600 mb-4">
                  Import leads from an Excel file. Make sure your file follows the correct format.
                </p>

                <div className="relative mb-4">
                  <select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200"
                    required
                  >
                    <option value="" disabled>Select a Project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.title}
                      </option>
                    ))}
                  </select>
                  <HiOutlineOfficeBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                </div>

                <div
                  className={`flex flex-col items-center justify-center w-full h-48 transition-colors duration-200 border-2 border-dashed rounded-xl cursor-pointer ${file ? 'border-secondary bg-secondary/5' : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  onClick={() => document.getElementById('fileInput').click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    setFile(e.dataTransfer.files[0]);
                  }}
                >
                  {file ? (
                    <div className="text-center">
                      <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 rounded-full bg-green-100">
                        <FaFileExcel className="text-2xl text-green-600" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                        className="mt-2 text-xs text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <FaUpload className="mb-3 text-4xl text-gray-400" />
                      <p className="mb-2 text-sm text-gray-700">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        Excel files only (XLSX, XLS)
                      </p>
                    </>
                  )}
                </div>

                {/* Hidden file input */}
                <input
                  id="fileInput"
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                />
              </div>

              <div className="flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleUpload}
                  disabled={!file || !selectedProject || loading}
                  className={`px-4 py-2 bg-secondary text-white font-medium rounded-xl shadow-sm flex items-center gap-2 ${!file || !selectedProject || loading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                >
                  {loading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FaUpload size={16} />
                      Upload File
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Add Lead Modal */}
      <AnimatePresence>
        {showAddLead && (
          <Modal title="Add New Lead" onClose={() => setShowAddLead(false)}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <AddLeadsForm
                onClose={() => {
                  setShowAddLead(false);
                  getLeads(currentPage, leadsPerPage);
                }}
              />
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {del && (
          <Modal title="Delete Lead" onClose={() => setDel(false)}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-6"
            >
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <FaExclamationTriangle className="text-red-600 text-xl" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Confirm Lead Deletion
                  </h3>
                  <p className="text-gray-600">
                    Are you sure you want to delete this lead? This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setDel(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDeleteLead}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white font-medium rounded-xl shadow-sm flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <MdDeleteForever size={18} />
                      Delete Lead
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default Leads;